
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEffect, useState } from "react";

interface VIPSectionProps {
  sectionNumber: number;
  selectedSeats: string[];
  onSeatClick: (seatId: string) => void;
  maxSeats: number;
}

interface SeatStatus {
  seatId: string;
  status: 'available' | 'reserved' | 'sold';
}

export const VIPSection = ({
  sectionNumber,
  selectedSeats,
  onSeatClick,
  maxSeats,
}: VIPSectionProps) => {
  const [seatStatuses, setSeatStatuses] = useState<SeatStatus[]>([]);
  const startCol = sectionNumber === 1 ? 1 : 6;
  const endCol = sectionNumber === 1 ? 5 : 10;

  // For now, we'll simulate seat statuses since the seat_reservations table doesn't exist
  // In a real implementation, this would fetch from the database
  const getSeatStatus = (seatId: string): 'available' | 'reserved' | 'sold' => {
    // Simulate some sold seats for demonstration
    const soldSeats = ['A1', 'A2', 'B6', 'C7'];
    if (soldSeats.includes(seatId)) {
      return 'sold';
    }
    return 'available';
  };

  const seats = [];
  const rows = ['A', 'B', 'C', 'D', 'E'];
  
  for (let row of rows) {
    const rowSeats = [];
    for (let col = startCol; col <= endCol; col++) {
      const seatId = `${row}${col}`;
      const status = getSeatStatus(seatId);
      const isAvailable = status === 'available' && (selectedSeats.length < maxSeats || selectedSeats.includes(seatId));
      const isSelected = selectedSeats.includes(seatId);
      
      const seatButton = (
        <button
          key={seatId}
          onClick={() => {
            if (status === 'sold') {
              toast.error("This seat has been sold");
              return;
            }
            if (!isAvailable && !selectedSeats.includes(seatId)) {
              toast.error("This seat is not available");
              return;
            }
            onSeatClick(seatId);
          }}
          disabled={status === 'sold' || (!isAvailable && !selectedSeats.includes(seatId))}
          className={`
            w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-xl text-xs sm:text-sm font-medium transition-all duration-300
            ${isSelected 
              ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white scale-105 animate-button-glow shadow-lg hover:shadow-xl"
              : status === 'sold'
                ? "bg-gray-600 text-gray-400 cursor-not-allowed opacity-75 hover:opacity-75"
                : isAvailable
                  ? "bg-gradient-to-r from-purple-100 to-purple-200 hover:from-purple-200 hover:to-purple-300 text-purple-900 border border-purple-300/50 hover:border-purple-400/50 shadow-sm hover:shadow-md hover:scale-105"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
            }
            transform hover:translate-y-[-2px]
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50
          `}
        >
          {seatId}
        </button>
      );

      if (status === 'sold') {
        rowSeats.push(
          <TooltipProvider key={seatId}>
            <Tooltip>
              <TooltipTrigger asChild>
                {seatButton}
              </TooltipTrigger>
              <TooltipContent>
                <p>Seat Sold</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      } else {
        rowSeats.push(seatButton);
      }
    }
    
    seats.push(
      <div key={row} className="flex gap-2 sm:gap-3 md:gap-4 justify-center items-center">
        <span className="text-white font-medium w-6 sm:w-8 flex items-center text-sm sm:text-base">{row}</span>
        <div className="flex gap-2 sm:gap-3 md:gap-4">{rowSeats}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 p-4 sm:p-6 md:p-8 bg-gray-800/50 backdrop-blur-sm rounded-xl border border-purple-500/20">
      <div className="text-white text-base sm:text-lg md:text-xl font-bold text-center mb-4 sm:mb-6">
        {sectionNumber === 1 ? 'Left of stage' : 'Right of stage'}
      </div>
      <div className="space-y-4 sm:space-y-6">
        {seats}
      </div>
      
      {/* Seat Legend */}
      <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-purple-500/20">
        <div className="flex flex-wrap gap-3 sm:gap-4 justify-center items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-r from-purple-100 to-purple-200 border border-purple-300/50"></div>
            <span className="text-xs sm:text-sm text-gray-300">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 animate-button-glow"></div>
            <span className="text-xs sm:text-sm text-gray-300">Selected</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-xl bg-gray-600 opacity-75"></div>
            <span className="text-xs sm:text-sm text-gray-300">Sold</span>
          </div>
        </div>
      </div>
    </div>
  );
};
